import { createElement, useLayoutEffect, useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { ConfigProvider } from 'antd';
import { useToken } from '@/common/helper';
import { isMonitorPath } from '@/constants/monitorPaths';
import { setDarkModeEnabled } from '@/redux-toolkit/slice/appearanceSlice';
import type { RootState, AppDispatch } from '@/redux-toolkit/store';
import type { Store } from 'redux';
import { zoomLogiAntdDarkTheme } from '@/theme/antdDarkTheme';
import { isDarkModeEnabled as isDarkModeFeatureEnabled } from '@/utils/featureFlags';

// antd's static `message` / `Modal.confirm` / `notification` render in their own
// React root, outside App's <ConfigProvider>. Giving them a theme with
// `ConfigProvider.config({ theme })` breaks the toast holder after a light/dark
// flip (no toast until a reload), so the holder is wrapped instead and reads the
// theme from the store itself.
let appearanceStore: Store<RootState> | null = null;
const subscribeToTheme = (listener: () => void): (() => void) =>
  appearanceStore ? appearanceStore.subscribe(listener) : () => undefined;
const isDarkNow = (): boolean =>
  appearanceStore?.getState().appearance.darkModeEnabled ?? false;

const StaticApiThemeHolder = ({ children }: { children: ReactNode }) => {
  const isDark = useSyncExternalStore(subscribeToTheme, isDarkNow);
  return createElement(
    ConfigProvider,
    { theme: isDark ? zoomLogiAntdDarkTheme : {} },
    children
  );
};

/**
 * Keeps the `appearance` redux slice in sync with the `dark_mode_enabled`
 * JWT claim, and mirrors it onto `<html data-theme>` for the SCSS side
 * (which can't read antd's in-JS theme tokens). antd's static `message` /
 * `Modal.confirm` / `notification` follow the same value through
 * registerStaticApiTheme(), since they render outside App's <ConfigProvider>.
 * Returns the current value so callers can also drive antd's ConfigProvider
 * algorithm from it.
 *
 * Gated on REACT_APP_ENABLE_DARK_MODE: with the flag off, a stored
 * `dark_mode_enabled=true` preference (e.g. synced from an account that
 * enabled it in UAT) is ignored and the app stays light. Without a usable
 * session (logged out, 401, expired) it is light as well: the pages reachable
 * then (login, ...) are light by design.
 *
 * These are layout effects so a theme change lands before the browser paints
 * (no frame in the previous theme after a login, a 401 or a toggle). The first
 * frame of a cold load is handled by the pre-paint script in index.html.
 */
/**
 * /monitor (and its globe version, /monitor_v2) is a shared wall TV, so it
 * looks the same whoever signed it in:
 * light, or dark with `?theme=dark`. Returns null on every other route. Only
 * the displayed theme changes; the user's saved preference is untouched and
 * applies again as soon as they leave /monitor.
 */
export const getMonitorThemeOverride = (
  pathname: string,
  search: string
): boolean | null => {
  if (!isMonitorPath(pathname)) return null;
  return new URLSearchParams(search).get('theme') === 'dark';
};

export default function useAppearanceSync(): boolean {
  const { decodedUserToken } = useToken();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const store = useStore<RootState>();
  const darkModeEnabled = useSelector(
    (state: RootState) => state.appearance.darkModeEnabled
  );

  useLayoutEffect(() => {
    appearanceStore = store;
    ConfigProvider.config({
      holderRender: (children) =>
        createElement(StaticApiThemeHolder, null, children),
    });
  }, [store]);

  useLayoutEffect(() => {
    if (!isDarkModeFeatureEnabled()) {
      // Force light regardless of what any other path (e.g. the Settings
      // save handler) may have dispatched, rather than merely skipping the
      // sync below -- a stale `true` from before the flag was flipped off
      // would otherwise stick until this effect found a reason to write
      // again.
      dispatch(setDarkModeEnabled(false));
      return;
    }
    // Deliberately not gated on this component's own `token` state: useToken()
    // hands every call site an independent local copy, so a login/SSO flow
    // that writes the token through its OWN useToken() instance (LoginForm,
    // OktaCallback, etc.) never updates this one, and the dispatch below
    // would never fire on a plain SPA login (only on a full page reload,
    // which happens to remount this hook fresh). Every such flow calls
    // navigate() though, so re-reading localStorage via decodedUserToken()
    // (always fresh, unlike the `token` state) on every route change is a
    // reliable signal that doesn't depend on which call site set it.
    //
    // No decoded token means no session: unAuth()/useUnauthorize navigate to
    // /login without a reload, so the previous user's `true` must not outlive
    // them -- antd would keep rendering dark tokens inside the light
    // `.zl-force-light-theme` login page.
    const decoded = decodedUserToken();
    const monitorDark = getMonitorThemeOverride(
      location.pathname,
      location.search
    );
    if (decoded && monitorDark !== null) {
      dispatch(setDarkModeEnabled(monitorDark));
      return;
    }
    dispatch(setDarkModeEnabled(!!decoded?.dark_mode_enabled));
  }, [location.pathname, location.search, decodedUserToken, dispatch]);

  useLayoutEffect(() => {
    document.documentElement.setAttribute(
      'data-theme',
      darkModeEnabled ? 'dark' : 'light'
    );
  }, [darkModeEnabled]);

  return darkModeEnabled;
}
